import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <zavod-bundle>: Buy 1 / 2 / 3 with a variant picker for every unit.
 *
 * Shows the bundle price for the chosen variants and adds them to the cart in one request.
 * It speaks the same cart events as Horizon's product form, so the cart drawer, the cart
 * icon and the cart page update and open the same way.
 *
 * The discount is not applied here: Shopify applies it (automatic discount or Bundler).
 */
class ZavodBundle extends HTMLElement {
  /** @type {Array<{id: number, title: string, available: boolean, price: number}>} */
  variants = [];

  /** @type {Map<string, {id: number, title: string, available: boolean, price: number}>} */
  variantsById = new Map();

  /** @type {string} */
  mainVariantId = '';

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    const data = this.querySelector('script[data-zb-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent || '[]');
    } catch (error) {
      console.error('[zavod-bundle] Bad variant data', error);
      return;
    }

    this.variantsById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
    this.mainVariantId = this.dataset.selectedVariantId || String(this.variants[0]?.id || '');

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('change', this.#onChange, { signal });
    this.addButton?.addEventListener('click', this.#onAdd, { signal });
    this.querySelector('[data-zb-buy]')?.addEventListener('click', this.#onBuyNow, { signal });

    // Horizon's variant picker fires a product-select event; follow the main color choice.
    const scope = this.closest('.shopify-section') || document;
    scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.update();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    clearTimeout(this.#resetTimer);
  }

  /** @returns {HTMLButtonElement | null} */
  get addButton() {
    return this.querySelector('[data-zb-add]');
  }

  /** @returns {HTMLElement[]} */
  get tiers() {
    return Array.from(this.querySelectorAll('[data-zb-tier]'));
  }

  /** @returns {HTMLElement | undefined} */
  get activeTier() {
    const checked = this.querySelector('.zb__input:checked');
    return /** @type {HTMLElement | undefined} */ (checked?.closest('[data-zb-tier]') || this.tiers[0]);
  }

  /**
   * The variant the theme's own picker has selected.
   * @returns {string}
   */
  currentMainVariantId() {
    const scope = this.closest('.shopify-section') || document;
    const input = /** @type {HTMLInputElement | null} */ (
      scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')
    );
    return input?.value || this.mainVariantId;
  }

  /**
   * Variant ids for every unit of a tier, in order.
   * @param {HTMLElement} tier
   * @returns {string[]}
   */
  unitVariantIds(tier) {
    const quantity = Number(tier.dataset.qty) || 1;
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(tier.querySelectorAll('select[data-zb-unit]')));

    if (selects.length === quantity) return selects.map((select) => select.value);

    return Array(quantity).fill(this.currentMainVariantId());
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * Re-renders prices, the selected state and the button for the current choices.
   */
  update() {
    const active = this.activeTier;

    for (const tier of this.tiers) {
      const ids = this.unitVariantIds(tier);
      const discount = Math.min(Math.max(Number(tier.dataset.discount) || 0, 0), 100);
      const full = ids.reduce((sum, id) => sum + (this.variantsById.get(id)?.price || 0), 0);
      const discounted = Math.round((full * (100 - discount)) / 100);

      const price = tier.querySelector('[data-zb-price]');
      const compare = tier.querySelector('[data-zb-compare]');
      const savings = tier.querySelector('[data-zb-savings]');

      if (price) price.textContent = this.money(discounted);
      if (compare) compare.textContent = this.money(full);
      if (savings && this.dataset.savingsText) {
        savings.textContent = this.dataset.savingsText
          .replace('[amount]', this.money(full - discounted))
          .replace('[percent]', String(discount));
      }

      tier.classList.toggle('is-selected', tier === active);
    }

    // Add-ons live inside the selected tier, like the free shipping strip.
    const addons = this.querySelector('[data-zb-addons]');
    const slot = active?.querySelector('[data-zb-addons-slot]');
    if (addons && slot && addons.parentElement !== slot) slot.append(addons);

    this.#updateButton();
  }

  #updateButton() {
    const button = this.addButton;
    const tier = this.activeTier;
    if (!button || !tier || button.getAttribute('aria-busy') === 'true') return;

    const soldOut = this.unitVariantIds(tier).some((id) => !this.variantsById.get(id)?.available);
    button.disabled = soldOut;
    const buy = this.querySelector('[data-zb-buy]');
    if (buy) buy.disabled = soldOut;

    const label = button.querySelector('[data-zb-add-label]');
    if (!label) return;

    if (!label.dataset.defaultText) label.dataset.defaultText = label.textContent || '';
    label.textContent = soldOut ? this.dataset.soldOutText || 'Sold out' : label.dataset.defaultText;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Add-on checkboxes don't change bundle prices.
    if (target instanceof HTMLInputElement && target.matches('[data-zb-addon]')) return;

    // Once a buyer picks a unit's color by hand, the main picker stops overriding it.
    if (target instanceof HTMLSelectElement && target.matches('[data-zb-unit]')) {
      target.dataset.touched = 'true';
    }

    this.#hideError();
    this.update();
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    event?.promise
      ?.then((/** @type {any} */ result) => {
        const id = result?.detail?.resource?.id ?? result?.variant?.id;
        if (!id) return;

        this.mainVariantId = String(id);

        for (const select of /** @type {NodeListOf<HTMLSelectElement>} */ (
          this.querySelectorAll('select[data-zb-unit]')
        )) {
          if (select.dataset.touched === 'true') continue;
          if (Array.from(select.options).some((option) => option.value === String(id) && !option.disabled)) {
            select.value = String(id);
          }
        }

        this.update();
      })
      .catch(() => {});
  };

  #onBuyNow = () => {
    const tier = this.activeTier;
    const buy = this.querySelector('[data-zb-buy]');
    if (!tier || !buy || buy.disabled) return;

    // Straight to checkout with only this bundle (a cart permalink), like Shopify's own Buy it now.
    const counts = new Map();
    for (const id of this.unitVariantIds(tier)) counts.set(id, (counts.get(id) || 0) + 1);
    for (const addon of this.querySelectorAll('input[data-zb-addon]:checked')) {
      if (addon.value) counts.set(addon.value, (counts.get(addon.value) || 0) + 1);
    }

    const lines = Array.from(counts, ([id, quantity]) => id + ':' + quantity).join(',');
    const root = window.Shopify?.routes?.root || '/';
    buy.setAttribute('aria-busy', 'true');
    window.setTimeout(() => buy.removeAttribute('aria-busy'), 4000);
    window.location.href = root + 'cart/' + lines;
  };

  #onAdd = async () => {
    const tier = this.activeTier;
    const button = this.addButton;
    if (!tier || !button || button.disabled) return;

    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const id of this.unitVariantIds(tier)) counts.set(id, (counts.get(id) || 0) + 1);

    // Ticked add-ons (e.g. shipping protection) go in the same request, one of each.
    for (const addon of /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[data-zb-addon]:checked')
    )) {
      if (addon.value) counts.set(addon.value, (counts.get(addon.value) || 0) + 1);
    }

    const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    this.#setBusy(true);
    this.#hideError();

    const routes = /** @type {any} */ (window).Theme?.routes || {};
    let added = false;

    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await this.#fetchCart(routes.cart_url || '/cart');

      const baseDetail = {
        items: cart.items,
        source: 'zavod-bundle',
        sourceId: this.id || 'zavod-bundle',
        itemCount: totalQuantity,
        productId: this.dataset.productId,
      };

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...baseDetail, didError: true },
        });
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#showError(result.description || result.message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...baseDetail, sections: result.sections, didError: false },
      });
      added = true;
    } catch (error) {
      console.error('[zavod-bundle]', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError();
    } finally {
      this.#setBusy(false);
      if (added) this.#showAdded();
    }
  };

  /**
   * @param {string} cartUrl
   * @returns {Promise<any>}
   */
  async #fetchCart(cartUrl) {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems?.fetchCartData) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const button = this.addButton;
    if (!button) return;

    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      this.#updateButton();
    }
  }

  #showAdded() {
    const label = this.addButton?.querySelector('[data-zb-add-label]');
    if (!label || !this.dataset.addedText) return;

    if (!label.dataset.defaultText) label.dataset.defaultText = label.textContent || '';
    label.textContent = this.dataset.addedText;

    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => this.#updateButton(), 2000);
  }

  /** @param {string} [message] */
  #showError(message) {
    const element = this.querySelector('[data-zb-error]');
    if (!element) return;

    element.textContent = message || this.dataset.errorText || 'Something went wrong.';
    element.hidden = false;
  }

  #hideError() {
    const element = this.querySelector('[data-zb-error]');
    if (element) element.hidden = true;
  }
}

if (!customElements.get('zavod-bundle')) {
  customElements.define('zavod-bundle', ZavodBundle);
}
